import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { ReportService } from '../../service/ReportService';
import ReactApexChart from 'react-apexcharts';
import { Range } from 'react-date-range';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { FilterBy, ViewType } from './TokenUsage';
import { TokenUsage } from '../../util/Types/TokenUsage';
import { generateData, generateLabels, getChartOptions } from './helpers';
import { Box } from '@mui/material';

interface Props {
  selectedDateRange: Range;
  queryString: string;
  differentYearsSelected: number[];
  filteredBy: FilterBy;
  viewType: ViewType;
}

const TextUsage: React.FC<Props> = ({
  selectedDateRange,
  queryString,
  differentYearsSelected,
  filteredBy,
  viewType,
}) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const authUser = useAuthStore(state => state.user);

  const [textUsage, setTextUsage] = useState<TokenUsage[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const getTextUsage = async () => {
      setLoaded(false);
      try {
        if (differentYearsSelected.length > 0) {
          let firstQueryString =
            viewType === ViewType.Company
              ? `companyId=${authUser?.selectedCompanyId}&year=${differentYearsSelected[0]}&months=1,2,3,4,5,6,7,8,9,10,11,12`
              : `year=${differentYearsSelected[0]}&months=1,2,3,4,5,6,7,8,9,10,11,12`;
          let secondQueryString =
            viewType === ViewType.Company
              ? `companyId=${authUser?.selectedCompanyId}&year=${differentYearsSelected[1]}&months=1,2,3,4,5,6,7,8,9,10,11,12`
              : `year=${differentYearsSelected[1]}&months=1,2,3,4,5,6,7,8,9,10,11,12`;
          const [firstResult, secondResult] = await Promise.all([
            ReportService.getTextUsage(authUser!.id, firstQueryString),
            ReportService.getTextUsage(authUser!.id, secondQueryString),
          ]);
          setTextUsage([...firstResult.data, ...secondResult.data]);
        } else {
          const response = await ReportService.getTextUsage(
            authUser!.id,
            queryString,
          );

          setTextUsage(response.data);
        }
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as any),
        });
      }

      setLoaded(true);
    };

    getTextUsage();
  }, [authUser, queryString, differentYearsSelected, viewType]);

  const { xAxisLabels, selectBy } = generateLabels(
    filteredBy,
    selectedDateRange,
    differentYearsSelected,
  );

  const series = [
    {
      name: 'Text',
      data: generateData(selectBy, xAxisLabels, textUsage, 'Text').map(
        el => el.usedTokens,
      ),
    },
  ];

  return loaded ? (
    <Box
      sx={{
        border: '1px solid rgba(234, 236, 240, 1)',
        padding: '16px',
        borderRadius: '4px',
        marginTop: '10px',
      }}
    >
      <ReactApexChart
        options={getChartOptions(
          xAxisLabels,
          ['#396AFC'],
          generateData(selectBy, xAxisLabels, textUsage, 'Text').map(
            el => el.price,
          ),
        )}
        series={series}
        type="bar"
        height={350}
      />
    </Box>
  ) : null;
};

export default TextUsage;
